import { useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import ToolButton from "./ToolButton";

const DeleteTool = ({
  mapElements,
  selectedElement,
  active,
  onClick,
  setMapElements,
  onAlert,
  open,
  setSelectedElement,
  currentMode,
  setCurrentMode,
  groupEraseShape,
  setGroupEraseShape,
}) => {
  const { t } = useTranslation();

  // Single Erase: selecting an element while in "delete" mode removes it.
  // Guard on the mode so it does NOT fire in "groupDelete" (rectangle/polygon).
  useEffect(() => {
    if (currentMode === "delete") handleDelete(selectedElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElement]);

  useEffect(() => {
    if (active) setSelectedElement({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function handleDelete(elementToDelete) {
    if (!elementToDelete || !elementToDelete.id) return;
    const newMapElements = mapElements.filter(
      (element) => element.id !== elementToDelete.id
    );

    if (
      elementToDelete.type !== "node" ||
      !existingEdgeConstraints(elementToDelete.id)
    ) {
      setMapElements(newMapElements);
    }
  }

  function existingEdgeConstraints(id) {
    const hasConstraints = mapElements.some(
      (mapElement) =>
        mapElement.type === "edge" &&
        (mapElement.startNodeId === id || mapElement.endNodeId === id)
    );
    if (hasConstraints)
      onAlert(
        "An edge has this knot attached. Disconnect the knot from the edge first.",
        "warning"
      );
    return hasConstraints;
  }

  // Toggle the whole erase tool: off when already active (single or group),
  // otherwise switch on and default to Single Erase.
  function handleEraseToggle(e) {
    if (active) {
      onClick(e, currentMode); // let the parent toggle back to "edit"
    } else {
      setCurrentMode("delete");
    }
  }

  const isGroup = currentMode === "groupDelete";

  return (
    <Box className="relative flex flex-col items-center justify-center">
      {/* Sub-controls appear above Erase once the tool is active.
          Single (left) and Group (right), staggered above Erase. */}
      {active && (
        <Box
          className="absolute flex flex-row items-end gap-8"
          sx={{ bottom: "100%", mb: 1, left: "50%", transform: "translateX(-50%)" }}
        >
          <ToolButton
            src={"/assets/svg/tools/erase-single.svg"}
            label={t("Single")}
            onClick={() => setCurrentMode("delete")}
            active={currentMode === "delete"}
            open={open}
            raiseOnActive={false}
          />
          {/* Group, with the shape switch (Rectangle / Polygon) placed above it */}
          <Box className="relative flex flex-col items-center">
            {isGroup && (
              <Box
                className="absolute flex flex-row items-end gap-8"
                sx={{
                  bottom: "100%",
                  mb: 1,
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <ToolButton
                  src={"/assets/svg/tools/shape-rectangle.svg"}
                  label=""
                  onClick={() => setGroupEraseShape("rectangle")}
                  active={groupEraseShape === "rectangle"}
                  open={open}
                  size={20}
                  raiseOnActive={false}
                />
                <ToolButton
                  src={"/assets/svg/tools/shape-polygon.svg"}
                  label=""
                  onClick={() => setGroupEraseShape("polygon")}
                  active={groupEraseShape === "polygon"}
                  open={open}
                  size={20}
                  raiseOnActive={false}
                />
              </Box>
            )}
            <ToolButton
              src={"/assets/svg/tools/erase-group.svg"}
              label={t("Group")}
              onClick={() => setCurrentMode("groupDelete")}
              active={isGroup}
              open={open}
              raiseOnActive={false}
            />
          </Box>
        </Box>
      )}
      <ToolButton
        src={"/assets/svg/tools/erase.svg"}
        label="Erase"
        onClick={handleEraseToggle}
        active={active}
        open={open}
      />
    </Box>
  );
};
export default DeleteTool;
